import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  MenuItem,
  TextField,
  LinearProgress,
  Alert,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  UploadFile,
  Delete,
  Download,
  CheckCircle,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  ArrowBack,
  Visibility,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import documentService from "../../services/documentService";
import useShipments from "../../hooks/useShipments";
import PageHeader from "../../components/common/PageHeader";
import StatusChip from "../../components/common/StatusChip";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const DOC_TYPES = [
  { value: "invoice", label: "Purchase Invoice" },
  { value: "other", label: "Other Document" },
];

const REQUIRED_DOCS = [
  {
    type: "invoice",
    label: "Purchase Invoice",
    required: true,
    note: "Required for customs",
  },
  {
    type: "other",
    label: "Other Document",
    required: false,
    note: "Optional support document",
  },
];

const FileIcon = ({ mimeType }) => {
  if (mimeType === "application/pdf")
    return <PictureAsPdf sx={{ color: "#E53935", fontSize: 22 }} />;
  if (mimeType?.startsWith("image/"))
    return <Image sx={{ color: "#1976d2", fontSize: 22 }} />;
  return <InsertDriveFile sx={{ color: "#757575", fontSize: 22 }} />;
};

const CustomerUpload = () => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { shipments, load } = useShipments();

  const [selectedShipment, setSelectedShipment] = useState(shipmentId || "");
  const [docType, setDocType] = useState("invoice");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  useEffect(() => {
    load({ limit: 50 });
  }, []);

  useEffect(() => {
    if (selectedShipment) fetchDocs(selectedShipment);
  }, [selectedShipment]);

  const fetchDocs = async (sid) => {
    setLoadingDocs(true);
    try {
      const { data } = await documentService.getForShipment(sid);
      setUploadedDocs(data.data.documents || []);
    } catch {
      setUploadedDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length > 0) {
        enqueueSnackbar("Some files were rejected (check type/size limits)", {
          variant: "warning",
        });
      }
      setPendingFiles((p) => [
        ...p,
        ...accepted.map((f) =>
          Object.assign(f, { preview: URL.createObjectURL(f) }),
        ),
      ]);
    },
    [enqueueSnackbar],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: 10 * 1024 * 1024,
  });

  const removePending = (idx) => {
    setPendingFiles((p) => p.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!selectedShipment) {
      enqueueSnackbar("Please select a shipment", { variant: "warning" });
      return;
    }
    if (pendingFiles.length === 0) {
      enqueueSnackbar("Please add files to upload", { variant: "warning" });
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      await documentService.upload(
        selectedShipment,
        pendingFiles,
        docType,
        setUploadProgress,
      );
      enqueueSnackbar(`${pendingFiles.length} file(s) uploaded successfully!`, {
        variant: "success",
      });
      setPendingFiles([]);
      setUploadProgress(100);
      await fetchDocs(selectedShipment);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Upload failed", {
        variant: "error",
      });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { data } = await documentService.download(doc._id);
      window.open(data.data.url, "_blank");
    } catch {
      enqueueSnackbar("Download failed", { variant: "error" });
    }
  };

  const handleView = async (doc) => {
    try {
      const { data } = await documentService.view(doc._id);
      window.open(data.data.url, "_blank");
    } catch {
      enqueueSnackbar("Failed to open document", { variant: "error" });
    }
  };

  const handleDelete = async (docId) => {
    try {
      await documentService.delete(docId);
      enqueueSnackbar("Document deleted", { variant: "success" });
      setUploadedDocs((p) => p.filter((d) => d._id !== docId));
    } catch {
      enqueueSnackbar("Failed to delete", { variant: "error" });
    }
  };

  const uploadedTypes = uploadedDocs.map((d) => d.type);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Upload Documents"
        subtitle="Upload purchase invoices or other shipment documents"
        action={
          <Button
            startIcon={<ArrowBack />}
            variant="outlined"
            size="small"
            onClick={() => navigate("/dashboard/shipments")}
          >
            Back to Shipments
          </Button>
        }
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
          gap: 2.5,
        }}
      >
        {/* Left - Upload area */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Shipment selector */}
          <Card>
            <CardContent
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                select
                label="Select Shipment"
                value={selectedShipment}
                onChange={(e) => setSelectedShipment(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
                size="small"
              >
                <MenuItem value="">— Choose a shipment —</MenuItem>
                {shipments.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.trackingNumber} —{" "}
                    {s.packageDescription || s.vendor?.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Document Type"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                sx={{ width: 220 }}
                size="small"
              >
                {DOC_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </CardContent>
          </Card>

          {/* Drop zone */}
          <Card>
            <CardContent>
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "rgba(13,98,155,0.04)",
                  },
                }}
              >
                <input {...getInputProps()} />
                <UploadFile
                  sx={{
                    fontSize: 44,
                    color: isDragActive ? "primary.main" : "text.disabled",
                    mb: 1.5,
                  }}
                />
                <Typography fontWeight={600} mb={0.5}>
                  {isDragActive
                    ? "Drop files here…"
                    : "Drag & drop files here, or click to browse"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, JPG, PNG, WEBP · Max 10MB per file · Up to 10 files
                </Typography>
              </Box>

              {/* Pending files */}
              {pendingFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    Ready to upload ({pendingFiles.length})
                  </Typography>
                  {pendingFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: "background.default",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <FileIcon mimeType={file.type} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024).toFixed(0)} KB
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removePending(idx)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Progress */}
              {uploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ height: 6, borderRadius: 99, mb: 0.75 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Uploading… {uploadProgress}%
                  </Typography>
                </Box>
              )}

              {pendingFiles.length > 0 && (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2.5 }}
                  onClick={handleUpload}
                  disabled={uploading || !selectedShipment}
                >
                  {uploading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    `Upload ${pendingFiles.length} File${pendingFiles.length > 1 ? "s" : ""}`
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Uploaded documents */}
          {selectedShipment && (
            <Card>
              <CardHeader
                title={
                  <Typography fontWeight={700} fontSize={14}>
                    Uploaded Documents
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" color="text.secondary">
                    {uploadedDocs.length} file(s)
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              {loadingDocs ? (
                <SkeletonLoader rows={4} />
              ) : uploadedDocs.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2.5 }}
                >
                  No documents uploaded yet for this shipment.
                </Typography>
              ) : (
                <Box sx={{ px: 2, pb: 2 }}>
                  {uploadedDocs.map((doc) => (
                    <Box
                      key={doc._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.25,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <FileIcon mimeType={doc.mimeType} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {doc.originalName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {DOC_TYPES.find((t) => t.value === doc.type)?.label ||
                            doc.type}{" "}
                          · {(doc.size / 1024).toFixed(0)} KB ·{" "}
                          {dayjs(doc.createdAt).format("MMM D, YYYY")}
                        </Typography>
                      </Box>
                      {doc.isVerified && (
                        <CheckCircle
                          sx={{
                            fontSize: 16,
                            color: "success.main",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {(doc.mimeType?.startsWith("image/") || doc.mimeType === "application/pdf") && (
                        <IconButton
                          size="small"
                          onClick={() => handleView(doc)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(doc._id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          )}
        </Box>

        {/* Right - Checklist & guide */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Document checklist */}
          {selectedShipment && (
            <Card>
              <CardHeader
                title={
                  <Typography fontWeight={700} fontSize={14}>
                    Document Checklist
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                {REQUIRED_DOCS.map((req) => {
                  const uploaded = uploadedTypes.includes(req.type);
                  return (
                    <Box
                      key={req.type}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: uploaded
                          ? "success.light"
                          : req.required
                            ? "error.light"
                            : "divider",
                        bgcolor: uploaded
                          ? "success.light"
                          : req.required
                            ? "#FFF5F5"
                            : "action.hover",
                      }}
                    >
                      <Typography sx={{ fontSize: 16, mt: 0.25 }}>
                        {uploaded ? "✅" : req.required ? "❌" : "⬜"}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            uploaded
                              ? "success.dark"
                              : req.required
                                ? "error.main"
                                : "text.primary"
                          }
                        >
                          {req.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {req.note}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          uploaded
                            ? "Uploaded"
                            : req.required
                              ? "Required"
                              : "Optional"
                        }
                        size="small"
                        sx={{
                          fontSize: 10,
                          height: 20,
                          bgcolor: uploaded
                            ? "success.main"
                            : req.required
                              ? "error.main"
                              : "grey.300",
                          color:
                            uploaded || req.required
                              ? "white"
                              : "text.secondary",
                        }}
                      />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Upload guide */}
          <Card>
            <CardHeader
              title={
                <Typography fontWeight={700} fontSize={14}>
                  Upload Guidelines
                </Typography>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="caption" fontWeight={600} display="block">
                  Invoice Requirements
                </Typography>
                <Typography variant="caption">
                  Must include merchant name, item descriptions, quantities,
                  unit prices, and total in USD.
                </Typography>
              </Alert>
              <Typography variant="body2" fontWeight={600} mb={1}>
                Accepted file types
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
                {["PDF", "JPG", "PNG", "WEBP"].map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      bgcolor: "info.light",
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
              {[
                "Maximum 10MB per file",
                "Up to 10 files per upload",
                "Ensure documents are legible",
                "Screenshots from email are accepted",
                "Keep file names descriptive",
              ].map((tip) => (
                <Typography
                  key={tip}
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  · {tip}
                </Typography>
              ))}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerUpload;
